import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { flatMap, map, take } from 'rxjs/operators';
import { Subject } from '../models/subject';
import { SubjectLink } from '../models/subject-link';
import { RelationshipService } from './relationship.service';
import { SubjectService } from './subject.service';

@Injectable()
export class SubjectLinkService {
  onSelected: ReplaySubject<any> = new ReplaySubject<any>(1, 100);

  constructor(
    private relationshipService: RelationshipService,
    private subjectService: SubjectService
  ) {}

  getLinkedSubjects(subject: Subject): Observable<any[]> {
    if (subject && subject.uid) {
      const linkedSubjects = [];
      const get$ = this.subjectService.getSubjectWithLinks(subject).pipe(
        take(1),
        map((data) => {
          const hydrate$ = [];

          // push a random observable onto the array - this ensures that we have something for the forkJoin
          hydrate$.push(of(null));

          if (data && data.subjectLinks) {
            data.subjectLinks.forEach((item) => {
              // check if has a linked subject and not already added to array of linked subjects with the same relationship
              if (
                item.linkedSubject &&
                linkedSubjects.findIndex(
                  (x) =>
                    x.id === item.linkedSubject.id &&
                    x.relationshipId === item.relationship.id
                ) < 0
              ) {
                const linkedSubject = new Subject(item.linkedSubject);
                // we need to calc required subjectData.displayValues for subject in case we need them for subject summary
                hydrate$.push(
                  this.subjectService.hydrate(linkedSubject).pipe(
                    take(1),
                    map((_) => {
                      linkedSubjects.push({
                        id: item.linkedSubjectId,
                        uid: linkedSubject.uid,
                        subjectTypeId: linkedSubject.subjectTypeId,
                        summary:
                          linkedSubject.summary || linkedSubject.reference,
                        relationship: item.relationship.name,
                        email: linkedSubject.getDataFieldValue('email'),
                        fullName: `${linkedSubject.getDataFieldValue(
                          'firstname'
                        )} ${
                          linkedSubject.getDataFieldValue('middlename')
                            ? linkedSubject.getDataFieldValue('middlename')
                            : ''
                        } ${linkedSubject.getDataFieldValue('lastname')}`,
                      });
                    })
                  )
                );
              }
            });
          }

          return hydrate$;
        })
      );

      // use forkJoin to wait for all the observables in the hydrate$ array to complete, then return the linkedSubjects
      return get$.pipe(
        take(1),
        flatMap((hydrate) => {
          return forkJoin(hydrate).pipe(
            map((_) => {
              return linkedSubjects;
            })
          );
        })
      );
    } else {
      return of(null);
    }
  }

  hydrate(item: SubjectLink): Observable<SubjectLink> {
    const hydrate$ = [];
    if (
      item.relationshipId &&
      (!item.relationship || item.relationship.id !== item.relationshipId)
    ) {
      hydrate$.push(
        this.relationshipService.getById(item.relationshipId).pipe(
          take(1),
          map((relationship) => {
            item.relationship = relationship;
            return item;
          })
        )
      );
    }

    if (hydrate$.length > 0) {
      return forkJoin(hydrate$).pipe(
        take(1),
        map((_) => {
          return item;
        })
      );
    } else {
      return of(item);
    }
  }
}
